import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate, inr } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/app/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar as CalIcon, Play, Check, Loader2, Circle, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { PriorityBadge, PriorityDot } from "@/components/app/priority";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const WORK_TYPES = [
  { key: "Structure Installation", short: "Structural", color: "#7C3AED" },
  { key: "Panel Installation", short: "Panel Fitting", color: "#F59E0B" },
  { key: "Inverter Installation", short: "Inverter Fitting", color: "#10B981" },
  { key: "Electrical Works", short: "Electrical", color: "#EF4444" },
];

function statusOf(stage) {
  return stage?.status || (stage?.completed ? "completed" : "pending");
}

export default function WorkScheduler() {
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [filterWork, setFilterWork] = useState("all");

  const load = () => api.get("/projects?lite=1").then((r) => setProjects(r.data.filter((p) => (p.completion_pct ?? 0) < 100)));
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    return projects
      .filter((p) => {
        if (!q.trim()) return true;
        const s = q.toLowerCase();
        return [p.customer_name, p.location, p.qr_code, p.project_manager].some(
          (v) => v && String(v).toLowerCase().includes(s)
        );
      })
      .map((p) => {
        const works = WORK_TYPES.map((w) => p.stages?.find((s) => s.key === w.key)).filter(Boolean);
        return { project: p, works };
      });
  }, [projects, q]);

  const counts = useMemo(() => {
    const c = { total: 0, pending: 0, in_progress: 0, completed: 0 };
    rows.forEach(({ project }) => {
      WORK_TYPES.forEach((w) => {
        const stage = project.stages?.find((s) => s.key === w.key);
        if (!stage) return;
        c.total++;
        const st = statusOf(stage);
        c[st]++;
      });
    });
    return c;
  }, [rows]);

  const update = async (pid, stage_key, patch) => {
    try {
      await api.post(`/projects/${pid}/stage`, { stage_key, ...patch });
      toast.success(`${stage_key} updated`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div data-testid="work-scheduler-page">
      <PageHeader
        eyebrow="Field Execution"
        title="Work Scheduler"
        subtitle="Schedule and track structural, panel, inverter and electrical work across active projects. Set planned dates, start work, mark complete."
      />
      <div className="p-4 sm:p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard testid="ws-total" label="Total Works" value={counts.total} />
          <StatCard testid="ws-pending" label="Pending" value={counts.pending} accent="warning" />
          <StatCard testid="ws-progress" label="In Progress" value={counts.in_progress} accent="primary" />
          <StatCard testid="ws-done" label="Completed" value={counts.completed} accent="success" />
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search project / customer / location…"
              className="rounded-none h-10 pl-9"
              data-testid="work-search"
            />
          </div>
          <Select value={filterWork} onValueChange={setFilterWork}>
            <SelectTrigger className="rounded-none h-10 w-full sm:w-56" data-testid="work-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Work Types</SelectItem>
              {WORK_TYPES.map((w) => <SelectItem key={w.key} value={w.key}>{w.short}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="today" className="w-full">
          <TabsList className="rounded-none border border-[#E5E7EB] bg-white p-0">
            <TabsTrigger value="today" className="rounded-none data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white uppercase tracking-widest text-xs font-bold px-6 h-10" data-testid="ws-tab-today">Today</TabsTrigger>
            <TabsTrigger value="tomorrow" className="rounded-none data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white uppercase tracking-widest text-xs font-bold px-6 h-10" data-testid="ws-tab-tomorrow">Tomorrow</TabsTrigger>
            <TabsTrigger value="grid" className="rounded-none data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white uppercase tracking-widest text-xs font-bold px-6 h-10" data-testid="ws-tab-grid">All Projects</TabsTrigger>
            <TabsTrigger value="calendar" className="rounded-none data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white uppercase tracking-widest text-xs font-bold px-6 h-10" data-testid="ws-tab-calendar">Calendar</TabsTrigger>
          </TabsList>

          <TabsContent value="today" className="mt-4">
            <DayList rows={rows} dayOffset={0} title="Today's Work" filterWork={filterWork} onUpdate={update} />
          </TabsContent>
          <TabsContent value="tomorrow" className="mt-4">
            <DayList rows={rows} dayOffset={1} title="Tomorrow's Work" filterWork={filterWork} onUpdate={update} />
          </TabsContent>

          <TabsContent value="grid" className="mt-4">
            <div className="space-y-4">
          {rows.map(({ project: p, works }) => (
            <div key={p.id} className="flat-card" data-testid={`ws-project-${p.id}`}>
              <div className="px-5 py-4 border-b border-[#E5E7EB] flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="label-eyebrow">{p.qr_code}</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/projects/${p.id}`} className="text-lg font-black tracking-tight hover:underline">{p.customer_name}</Link>
                    <PriorityBadge priority={p.priority || "Low"} />
                  </div>
                  <div className="text-xs text-[#4B5563]">{p.capacity_kw} kW · {p.location} · {inr(p.contract_value_inr)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-1.5 w-32 bg-[#F3F4F6]">
                    <div className="h-full bg-[#002FA7]" style={{ width: `${p.completion_pct}%` }} />
                  </div>
                  <span className="text-xs font-bold">{p.completion_pct}%</span>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-[#E5E7EB]">
                {works
                  .filter((s) => filterWork === "all" || s.key === filterWork)
                  .map((s, i) => {
                    const meta = WORK_TYPES.find((w) => w.key === s.key);
                    return (
                      <WorkCell
                        key={s.key}
                        project={p}
                        stage={s}
                        meta={meta}
                        onUpdate={(patch) => update(p.id, s.key, patch)}
                      />
                    );
                  })}
              </div>
            </div>
          ))}
          {rows.length === 0 && (
            <div className="flat-card p-12 text-center text-sm text-[#9CA3AF]">
              All projects complete! No outstanding work.
            </div>
          )}
            </div>
          </TabsContent>

          <TabsContent value="calendar" className="mt-4">
            <CalendarView rows={rows} onUpdate={update} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function WorkCell({ project, stage, meta, onUpdate }) {
  const status = statusOf(stage);
  const isDone = status === "completed";
  const isProg = status === "in_progress";
  const Icon = isDone ? Check : isProg ? Loader2 : Circle;
  const color = isDone ? "#10B981" : isProg ? "#002FA7" : "#9CA3AF";
  const bg = isDone ? "#D1FAE5" : isProg ? "#DBEAFE" : "#F3F4F6";
  const testKey = stage.key.replace(/\s+/g, "-");

  const [worker, setWorker] = useState(stage.responsible || "");
  useEffect(() => { setWorker(stage.responsible || ""); }, [stage.responsible]);

  return (
    <div className="p-3 flex flex-col gap-2 min-w-0" data-testid={`ws-cell-${project.id}-${testKey}`}>
      {/* Line 1: identity + status + actions */}
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center" style={{ background: bg }}>
          <Icon className={`w-4 h-4 ${isProg ? "animate-spin" : ""}`} style={{ color }} strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold tracking-tight truncate leading-tight" style={{ color: meta?.color }}>{meta?.short}</div>
          <span className="text-[10px] uppercase tracking-widest font-bold leading-tight" style={{ color }}>
            {status.replace("_", " ")}
          </span>
        </div>
        <div className="flex gap-0.5 flex-shrink-0">
          {status === "pending" && (
            <Button onClick={() => onUpdate({ status: "in_progress" })} size="sm" variant="outline" className="rounded-none border-[#002FA7] text-[#002FA7] h-7 w-7 p-0" data-testid={`ws-start-${project.id}-${testKey}`} title="Start">
              <Play className="w-3.5 h-3.5" />
            </Button>
          )}
          {!isDone && (
            <Button onClick={() => onUpdate({ status: "completed" })} size="sm" variant="outline" className="rounded-none border-[#10B981] text-[#10B981] h-7 w-7 p-0" data-testid={`ws-done-${project.id}-${testKey}`} title="Done">
              <Check className="w-3.5 h-3.5" />
            </Button>
          )}
          {isDone && (
            <Button onClick={() => onUpdate({ status: "pending" })} size="sm" variant="ghost" className="rounded-none font-bold h-7 w-7 p-0 text-[#4B5563]" data-testid={`ws-reopen-${project.id}-${testKey}`} title="Reopen">
              ↺
            </Button>
          )}
        </div>
      </div>

      {/* Line 2: planned date + schedule */}
      <div className="flex items-center justify-between gap-2 text-xs border-t border-[#F3F4F6] pt-2">
        <span className="text-[#0a0a0a] font-semibold truncate">{stage.planned_date ? fmtDate(stage.planned_date) : "Not set"}</span>
        <ScheduleRow value={stage.planned_date} onChange={(d) => onUpdate({ planned_date: d })} testid={`ws-plan-${project.id}-${testKey}`} />
      </div>

      {/* Line 3: worker name */}
      <Input
        value={worker}
        onChange={(e) => setWorker(e.target.value)}
        onBlur={() => { if (worker !== (stage.responsible || "")) onUpdate({ responsible: worker }); }}
        placeholder="Worker"
        className="rounded-none h-8 text-xs w-full min-w-0"
        data-testid={`ws-worker-${project.id}-${testKey}`}
      />
    </div>
  );
}

function ScheduleRow({ value, onChange, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ? value.slice(0, 10) : "");

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          type="date"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="rounded-none h-6 text-[10px] px-1"
          data-testid={testid}
        />
        <button
          onClick={() => { onChange(draft || null); setEditing(false); }}
          className="text-[10px] font-bold text-[#002FA7] uppercase tracking-widest px-1"
        >
          OK
        </button>
      </div>
    );
  }
  return (
    <button
      onClick={() => setEditing(true)}
      className="font-semibold text-[#002FA7] hover:underline flex items-center gap-1"
    >
      <CalIcon className="w-3 h-3" /> {value ? "Edit" : "Set"}
    </button>
  );
}

function startOfWeek(d) {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  const day = x.getDay(); // 0 Sun .. 6 Sat
  const diff = (day === 0 ? -6 : 1 - day);
  x.setDate(x.getDate() + diff);
  return x;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function ymd(d) {
  // For date-only strings ("2025-06-17") or ISO strings, slice the date portion
  // to avoid UTC conversion shifting the day in non-UTC timezones (e.g., IST +5:30).
  if (typeof d === "string") return d.slice(0, 10);
  // For Date objects, use LOCAL components so the cell key matches the displayed day.
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function sameDayYmd(a, b) { return a && b && ymd(a) === ymd(b); }

function DayList({ rows, dayOffset, title, filterWork, onUpdate }) {
  // Flatten {project, stage} entries whose planned_date matches the target day
  const target = addDays(new Date(), dayOffset);
  const targetYmd = ymd(target);
  const items = rows.flatMap(({ project }) =>
    (project.stages || [])
      .filter((s) => WORK_TYPES.some((w) => w.key === s.key) && s.planned_date)
      .filter((s) => filterWork === "all" || s.key === filterWork)
      .filter((s) => ymd(s.planned_date) === targetYmd)
      .map((s) => ({ project, stage: s, meta: WORK_TYPES.find((w) => w.key === s.key) }))
  );

  return (
    <div data-testid={`ws-daylist-${dayOffset === 0 ? "today" : "tomorrow"}`}>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="label-eyebrow">{title}</div>
          <div className="text-xs text-[#4B5563] mt-1">
            {target.toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-widest font-bold text-[#4B5563]">
          {items.length} work{items.length === 1 ? "" : "s"} scheduled
        </span>
      </div>

      {items.length === 0 ? (
        <div className="flat-card p-10 text-center text-sm text-[#9CA3AF]" data-testid={`ws-daylist-empty-${dayOffset}`}>
          No work scheduled for {dayOffset === 0 ? "today" : "tomorrow"}.
          Use the <b>All Projects</b> tab or the <b>Calendar</b> to schedule planned work.
        </div>
      ) : (
        <div className="flat-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[#4B5563] border-b border-[#E5E7EB]">
                <th className="text-left px-4 py-2 font-bold">Project</th>
                <th className="text-left px-3 py-2 font-bold">Work</th>
                <th className="text-left px-3 py-2 font-bold">Status</th>
                <th className="text-left px-3 py-2 font-bold">Worker</th>
                <th className="text-left px-3 py-2 font-bold">Scheduled Date</th>
                <th className="text-right px-3 py-2 font-bold pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(({ project, stage, meta }) => (
                <DayRow
                  key={`${project.id}-${stage.key}`}
                  project={project}
                  stage={stage}
                  meta={meta}
                  onUpdate={(patch) => onUpdate(project.id, stage.key, patch)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DayRow({ project, stage, meta, onUpdate }) {
  const status = statusOf(stage);
  const isDone = status === "completed";
  const isProg = status === "in_progress";
  const color = isDone ? "#10B981" : isProg ? "#002FA7" : "#9CA3AF";
  const bg = isDone ? "#D1FAE5" : isProg ? "#DBEAFE" : "#F3F4F6";
  const testKey = stage.key.replace(/\s+/g, "-");

  const [worker, setWorker] = useState(stage.responsible || "");
  useEffect(() => { setWorker(stage.responsible || ""); }, [stage.responsible]);

  const [date, setDate] = useState(stage.planned_date ? stage.planned_date.slice(0, 10) : "");
  useEffect(() => { setDate(stage.planned_date ? stage.planned_date.slice(0, 10) : ""); }, [stage.planned_date]);

  return (
    <tr className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]" data-testid={`ws-day-row-${project.id}-${testKey}`}>
      <td className="px-4 py-3 align-top">
        <div className="label-eyebrow text-[9px]">{project.qr_code}</div>
        <Link to={`/projects/${project.id}`} className="font-bold tracking-tight hover:underline text-sm">{project.customer_name}</Link>
        <div className="text-[11px] text-[#4B5563]">{project.capacity_kw} kW · {project.location}</div>
      </td>
      <td className="px-3 py-3 align-top">
        <span className="font-bold tracking-tight text-sm" style={{ color: meta?.color }}>{meta?.short}</span>
      </td>
      <td className="px-3 py-3 align-top">
        <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1" style={{ background: bg, color }}>
          {status.replace("_", " ")}
        </span>
      </td>
      <td className="px-3 py-3 align-top w-44">
        <Input
          value={worker}
          onChange={(e) => setWorker(e.target.value)}
          onBlur={() => { if (worker !== (stage.responsible || "")) onUpdate({ responsible: worker }); }}
          placeholder="Assign worker"
          className="rounded-none h-8 text-xs"
          data-testid={`ws-day-worker-${project.id}-${testKey}`}
        />
      </td>
      <td className="px-3 py-3 align-top w-40">
        <Input
          type="date"
          value={date}
          onChange={(e) => {
            setDate(e.target.value);
            onUpdate({ planned_date: e.target.value || null });
          }}
          className="rounded-none h-8 text-xs"
          data-testid={`ws-day-date-${project.id}-${testKey}`}
        />
      </td>
      <td className="px-3 py-3 align-top pr-4">
        <div className="flex gap-1 justify-end">
          {status === "pending" && (
            <Button onClick={() => onUpdate({ status: "in_progress" })} size="sm" variant="outline"
                    className="rounded-none border-[#002FA7] text-[#002FA7] h-7 px-2 text-[10px] font-bold uppercase tracking-widest"
                    data-testid={`ws-day-start-${project.id}-${testKey}`}>
              <Play className="w-3 h-3 mr-1" /> Start
            </Button>
          )}
          {!isDone && (
            <Button onClick={() => onUpdate({ status: "completed" })} size="sm" variant="outline"
                    className="rounded-none border-[#10B981] text-[#10B981] h-7 px-2 text-[10px] font-bold uppercase tracking-widest"
                    data-testid={`ws-day-done-${project.id}-${testKey}`}>
              <Check className="w-3 h-3 mr-1" /> Done
            </Button>
          )}
          {isDone && (
            <Button onClick={() => onUpdate({ status: "pending" })} size="sm" variant="ghost"
                    className="rounded-none h-7 px-2 text-[10px] font-bold uppercase tracking-widest text-[#4B5563]"
                    data-testid={`ws-day-reopen-${project.id}-${testKey}`}>
              ↺ Reopen
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function CalendarView({ rows, onUpdate }) {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [edit, setEdit] = useState(null);
  const days = [0, 1, 2, 3, 4, 5, 6].map((i) => addDays(weekStart, i));

  // Flatten {project, stage} entries that have planned_date within or anywhere
  const flat = rows.flatMap(({ project }) =>
    (project.stages || [])
      .filter((s) => WORK_TYPES.some((w) => w.key === s.key) && s.planned_date)
      .map((s) => ({ project, stage: s, meta: WORK_TYPES.find((w) => w.key === s.key) }))
  );

  const itemsFor = (day) => flat.filter((x) => sameDayYmd(x.stage.planned_date, day));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <div className="lg:col-span-1 flat-card p-4 max-h-[600px] overflow-y-auto">
        <div className="label-eyebrow mb-3">Active Projects ({rows.length})</div>
        <div className="space-y-2">
          {rows.map(({ project: p }) => (
            <div key={p.id} className="border border-[#E5E7EB] p-2 text-xs" data-testid={`cal-project-${p.id}`}>
              <div className="flex items-center gap-1.5">
                <PriorityDot priority={p.priority || "Low"} />
                <div className="font-bold tracking-tight truncate flex-1">{p.customer_name}</div>
              </div>
              <div className="text-[#4B5563]">{p.capacity_kw} kW · {p.qr_code}</div>
              <div className="mt-1 flex gap-1 flex-wrap">
                {WORK_TYPES.map((w) => {
                  const s = p.stages?.find((x) => x.key === w.key);
                  if (!s) return null;
                  const c = statusOf(s) === "completed" ? "#10B981" : statusOf(s) === "in_progress" ? "#002FA7" : "#9CA3AF";
                  return <span key={w.key} className="text-[8px] uppercase font-bold px-1" style={{ background: "#F3F4F6", color: c }} title={w.short}>{w.short[0]}</span>;
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="lg:col-span-3 flat-card">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <div className="label-eyebrow">Week of {weekStart.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>
          <div className="flex gap-1">
            <button onClick={() => setWeekStart(addDays(weekStart, -7))} className="p-1 border border-[#E5E7EB]" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => setWeekStart(startOfWeek(new Date()))} className="px-3 text-[10px] uppercase tracking-widest font-bold border border-[#E5E7EB]" data-testid="cal-today">Today</button>
            <button onClick={() => setWeekStart(addDays(weekStart, 7))} className="p-1 border border-[#E5E7EB]" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="grid grid-cols-7 divide-x divide-[#E5E7EB] max-h-[560px] overflow-y-auto">
          {days.map((day) => {
            const items = itemsFor(day);
            const isToday = sameDayYmd(day, new Date());
            return (
              <div key={ymd(day)} className="min-h-[120px] p-2" data-testid={`cal-day-${ymd(day)}`}>
                <div className={`label-eyebrow mb-2 ${isToday ? "text-[#002FA7]" : ""}`}>
                  {day.toLocaleDateString("en-IN", { weekday: "short" })} {day.getDate()}
                </div>
                <div className="space-y-1">
                  {items.map(({ project, stage, meta }) => {
                    const c = statusOf(stage) === "completed" ? "#10B981" : statusOf(stage) === "in_progress" ? "#002FA7" : meta.color;
                    return (
                      <button
                        key={`${project.id}-${stage.key}`}
                        onClick={() => setEdit({ project, stage })}
                        className="block w-full text-left p-1.5 border-l-2 hover:bg-[#F9FAFB]"
                        style={{ borderColor: c }}
                        data-testid={`cal-item-${project.id}-${stage.key.replace(/\s+/g, "-")}`}
                      >
                        <div className="text-[10px] font-bold tracking-tight truncate" style={{ color: c }}>{meta.short}</div>
                        <div className="text-[10px] text-[#4B5563] truncate">{project.customer_name}</div>
                        <div className="text-[10px] font-semibold text-[#0a0a0a] truncate flex items-center gap-1">
                          👷 {stage.responsible || <span className="text-[#9CA3AF] font-normal italic">Unassigned</span>}
                        </div>
                      </button>
                    );
                  })}
                  {items.length === 0 && <div className="text-[9px] text-[#9CA3AF] italic">—</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {edit && <RescheduleDialog entry={edit} onClose={() => setEdit(null)} onUpdate={onUpdate} />}
    </div>
  );
}

function RescheduleDialog({ entry, onClose, onUpdate }) {
  const [date, setDate] = useState(entry.stage.planned_date ? entry.stage.planned_date.slice(0, 10) : "");
  const save = () => {
    onUpdate(entry.project.id, entry.stage.key, { planned_date: date || null });
    onClose();
  };
  const meta = WORK_TYPES.find((w) => w.key === entry.stage.key);
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-none border-[#0a0a0a]">
        <DialogHeader><DialogTitle className="font-black text-2xl tracking-tight">Reschedule</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div className="text-sm"><b>{entry.project.customer_name}</b> · {meta?.short}</div>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border border-[#0a0a0a] h-10 px-3 w-full text-sm" data-testid="cal-edit-date" />
          <div className="flex gap-2">
            <Button onClick={save} className="flex-1 rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-10" data-testid="cal-edit-save">Save</Button>
            <Button onClick={() => { onUpdate(entry.project.id, entry.stage.key, { planned_date: null }); onClose(); }} variant="outline" className="rounded-none border-[#EF4444] text-[#EF4444] uppercase tracking-widest text-xs font-bold h-10" data-testid="cal-edit-clear">Clear</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
